import * as turf from '@turf/turf';

/**
 * @function euclideanDistance
 * @description Plain Euclidean distance between two [x, y] points.
 * @param {Array<number>} p1 - An `[x, y]` pair.
 * @param {Array<number>} p2 - An `[x, y]` pair.
 * **Called by:
 * @see {shoreSAnalysis/saga}
 * @memberof module:ShoreS
 * @returns {number}
 */
export const euclideanDistance = (p1, p2) => Math.hypot(p1[0] - p2[0], p1[1] - p2[1]);

const pointsToGeometry = (points) => {
  if (!points.length) return null;
  if (points.length === 1) return { type: 'Point', coordinates: points[0] };
  return { type: 'MultiPoint', coordinates: points };
};

/**
 * @function intersectTransectsToCoastlines
 * @description Pairs every transect with every coastline (cross join) and computes each
 * pair's intersection geometry via `turf.lineIntersect`. Kept as a flat
 * array of records — including records with `geometry: null` for
 * non-intersecting pairs, preserving the per-transect/per-time bookkeeping
 * for "this transect didn't see this coastline".
 * @param {GeoJSON.FeatureCollection<GeoJSON.LineString>} transectsFC
 * @param {GeoJSON.FeatureCollection<GeoJSON.LineString>} coastlinesFC
 * @param {{transectIdProp: (string|undefined), coastlineIdProp: (string|undefined)}} [options]
 * **Called by:
 * @see {shoreSAnalysis/saga}
 * @memberof module:ShoreS
 * @returns {Array<{transectId: *, coastlineId: *, geometry: GeoJSON.Point|GeoJSON.MultiPoint|null}>}
 */
export const intersectTransectsToCoastlines = (
  transectsFC,
  coastlinesFC,
  { transectIdProp = 'transect_id', coastlineIdProp = 'time' } = {},
) => {
  const records = [];
  (transectsFC.features || []).forEach((transect) => {
    const transectId = transect.properties[transectIdProp];
    (coastlinesFC.features || []).forEach((coastline) => {
      const coastlineId = coastline.properties[coastlineIdProp];
      let geometry = null;
      try {
        const intersections = turf.lineIntersect(transect, coastline);
        geometry = pointsToGeometry(intersections.features.map((f) => f.geometry.coordinates));
      } catch (e) {
        geometry = null;
      }
      records.push({ transectId, coastlineId, geometry });
    });
  });
  return records;
};

/**
 * @function intersectionClosestToReference
 * @description Given an intersection geometry that may carry multiple candidate points
 * (a coastline can cross a transect more than once), picks the single point
 * closest to a reference point (the transect's intersection with the
 * baseline).
 * @param {GeoJSON.Point|GeoJSON.MultiPoint|GeoJSON.LineString|null} geometry
 * @param {Array<number>|null} referencePoint - An `[x, y]` pair.
 * **Called by:
 * @see {keepOnlyIntersectionClosestToBaseline}
 * @memberof module:ShoreS
 * @returns {GeoJSON.Point|null}
 */
export const intersectionClosestToReference = (geometry, referencePoint) => {
  if (!geometry || !referencePoint) return null;

  if (geometry.type === 'Point') return geometry;

  if (geometry.type === 'MultiPoint' || geometry.type === 'LineString') {
    const closest = geometry.coordinates.reduce(
      (best, coord) =>
        best === null || euclideanDistance(coord, referencePoint) < euclideanDistance(best, referencePoint)
          ? coord
          : best,
      null,
    );
    return closest ? { type: 'Point', coordinates: closest } : null;
  }

  return null;
};

/**
 * @function intersectTransectsWithBaseline
 * @description Intersects each transect with the baseline once, producing a
 * `{ transectId: [x, y] }` map , both the reference point used by
 * `keepOnlyIntersectionClosestToBaseline` and the origin used by
 * `computeDistancesFromBaseline`.
 * @param {GeoJSON.FeatureCollection<GeoJSON.LineString>} transectsFC
 * @param {GeoJSON.Feature<GeoJSON.LineString>} baselineFeature
 * @param {string} [transectIdProp]
 * **Called by:
 * @see {shoreSAnalysis/saga}
 * @memberof module:ShoreS
 * @returns {Object<(string|number), Array<number>>} Keyed by transect id, each value an `[x, y]` pair.
 */
export const intersectTransectsWithBaseline = (transectsFC, baselineFeature, transectIdProp = 'transect_id') => {
  const baselinePointsByTransect = {};
  (transectsFC.features || []).forEach((transect) => {
    const transectId = transect.properties[transectIdProp];
    try {
      const intersections = turf.lineIntersect(transect, baselineFeature);
      if (intersections.features.length > 0) {
        baselinePointsByTransect[transectId] = intersections.features[0].geometry.coordinates;
      }
    } catch (e) {
      // no intersection for this transect — left unset
    }
  });
  return baselinePointsByTransect;
};

/**
 * @function keepOnlyIntersectionClosestToBaseline
 * @description For each intersection record, keeps only the candidate point closest to
 * the transect's baseline intersection.
 * @param {Array<{transectId, coastlineId, geometry}>} intersections
 * @param {Object<(string|number), Array<number>>} baselinePointsByTransect - Keyed by transect id, each value an `[x, y]` pair.
 * **Called by:
 * @see {shoreSAnalysis/saga}
 * @memberof module:ShoreS
 * @returns {Array<{transectId, coastlineId, geometry: GeoJSON.Point|null}>}
 */
export const keepOnlyIntersectionClosestToBaseline = (intersections, baselinePointsByTransect) =>
  intersections.map((record) => ({
    ...record,
    geometry: intersectionClosestToReference(record.geometry, baselinePointsByTransect[record.transectId]),
  }));

/**
 * @function computeDistancesFromBaseline
 * @description Computes, per transect/time, the distance from the transect's baseline
 * intersection to its coastline intersection (`null` when either point is
 * missing). Used for both modeled and reference coastlines.
 * @param {Object<(string|number), Array<number>>} baselinePointsByTransect - Keyed by transect id, each value an `[x, y]` pair.
 * @param {Array<{transectId, coastlineId, geometry: GeoJSON.Point|null}>} coastlineIntersections - Post `keepOnlyIntersectionClosestToBaseline`.
 * **Called by:
 * @see {shoreSAnalysis/saga}
 * @memberof module:ShoreS
 * @returns {Array<{transectId, time, distance: number|null}>}
 */
export const computeDistancesFromBaseline = (baselinePointsByTransect, coastlineIntersections) =>
  coastlineIntersections.map(({ transectId, coastlineId, geometry }) => {
    const baselinePoint = baselinePointsByTransect[transectId];
    const distance =
      geometry && baselinePoint ? euclideanDistance(geometry.coordinates, baselinePoint) : null;
    return { transectId, time: coastlineId, distance };
  });
